import type {
	PersonaDraft,
	PromptGenerationResultItem,
} from "@repo/core/client";
import { apiClient } from "@shared/lib/api-client";

export interface LocationData {
	city?: string;
	region?: string;
	country?: string;
}

/** `GET /api/ai-prompt-generator/generation/:generationId`. */
export interface PromptGenerationResult {
	id: string;
	status: "pending" | "running" | "complete" | "failed";
	frameworks: string[];
	resultCount: number | null;
	results: PromptGenerationResultItem[] | null;
	error: string | null;
	createdAt: string;
	/** The domain the generation was built for. */
	domain: string | null;
	includePersonas: boolean;
	/** Null while drafting, when not requested, or when drafting failed. */
	personaDrafts: PersonaDraft[] | null;
	/** Customer segments from the brand snapshot (complete generations). */
	segments: string[];
	/** The one results email for this generation was already sent. */
	emailSent: boolean;
}

/**
 * The three public routes this page calls, typed locally: the free tool only
 * needs the request shapes, and a plain `Response` back.
 */
interface PublicPromptGeneratorClient {
	generate: {
		$post: (
			args: {
				json: {
					domain: string;
					frameworks: string[];
					location?: LocationData;
					includePersonas: boolean;
				};
			},
			options?: { headers?: Record<string, string> },
		) => Promise<Response>;
	};
	generation: {
		":generationId": {
			$get: (args: {
				param: { generationId: string };
			}) => Promise<Response>;
			email: {
				$post: (args: {
					param: { generationId: string };
					json: { email: string };
				}) => Promise<Response>;
			};
		};
	};
}

const client = (
	apiClient as unknown as {
		"ai-prompt-generator": PublicPromptGeneratorClient;
	}
)["ai-prompt-generator"];

/** An API refusal with its HTTP status (and `code`, when the route sets one). */
export class PromptGeneratorApiError extends Error {
	readonly status: number;
	readonly code: string | null;
	constructor(message: string, status: number, code: string | null = null) {
		super(message);
		this.name = "PromptGeneratorApiError";
		this.status = status;
		this.code = code;
	}
}

async function errorFrom(
	res: Response,
	fallback: string,
): Promise<PromptGeneratorApiError> {
	const body = (await res.json().catch(() => ({}))) as {
		error?: string;
		code?: string;
	};
	return new PromptGeneratorApiError(
		body.error || fallback,
		res.status,
		body.code ?? null,
	);
}

/** PostHog's session id, when PostHog is loaded (Langfuse groups by it). */
export function posthogSessionId(): string | undefined {
	if (typeof window === "undefined") {
		return undefined;
	}
	const posthog = (
		window as unknown as {
			posthog?: { get_session_id?: () => string | undefined };
		}
	).posthog;
	try {
		return posthog?.get_session_id?.() || undefined;
	} catch {
		return undefined;
	}
}

export async function generatePrompts(input: {
	domain: string;
	frameworks: string[];
	location?: LocationData;
	includePersonas: boolean;
	sessionId?: string;
}): Promise<{ generationId: string; propertyId: string }> {
	const headers: Record<string, string> = {};
	if (input.sessionId) {
		headers["X-POSTHOG-SESSION-ID"] = input.sessionId;
	}

	const res = await client.generate.$post(
		{
			json: {
				domain: input.domain,
				frameworks: input.frameworks,
				location: input.location,
				includePersonas: input.includePersonas,
			},
		},
		{ headers },
	);

	if (!res.ok) {
		throw await errorFrom(
			res,
			"Failed to start prompt generation. Please try again.",
		);
	}

	return await res.json();
}

export async function fetchGenerationStatus(
	generationId: string,
): Promise<PromptGenerationResult> {
	const res = await client.generation[":generationId"].$get({
		param: { generationId },
	});

	if (!res.ok) {
		throw await errorFrom(res, "Failed to fetch generation status");
	}

	return await res.json();
}

/**
 * "Email me this panel". Resolves on 200; throws PromptGeneratorApiError
 * otherwise (409 = already sent for this generation).
 */
export async function emailGenerationResults(
	generationId: string,
	email: string,
): Promise<void> {
	const res = await client.generation[":generationId"].email.$post({
		param: { generationId },
		json: { email },
	});

	if (!res.ok) {
		throw await errorFrom(res, "We couldn't send that email.");
	}
}
